import React, { useEffect, useState } from 'react'
import { useTheme } from '@emotion/react'

import { GalleryThumbnails } from '../../content/Gallery/components/GalleryThumbnails'
import { GalleryCaption } from '../../content/Gallery/components/GalleryCaption'
import {
    GalleryImages,
    numPerPage,
} from '../../content/Gallery/components/GalleryImages'

import { Profile } from '@news-mono/web-common'

import { Picture } from '../../content/Picture/Picture'
import { Byline } from '../../publication/Byline/Byline'

import {
    StyledGallery,
    StyledHeroWrap,
    StyledHeroCaptionContainer,
    StyledTitleBar,
    StyledSharing,
    StyledIcon,
    StyledTitle,
    StyledShowMoreButton,
    StyledGalleryContentWrapper,
} from '../../content/Gallery/Gallery.styled'
import { Skeleton } from '../../content/Skeleton/Skeleton'
import { StyledButton } from '../../buttons/Button/Button.styled'
import { smoothScrollTo } from '../../__helpers/smoothscroll'
import { ImageSourceWithCaptions } from '@news-mono/web-common'
import { GalleryEvent } from '@news-mono/web-common'
import { DataLayerEventName } from '@news-mono/web-common'
import { themedValue } from '../../__styling/themed-value'
import { PicturePlaceholder } from '../Picture/PicturePlaceholder'
import { ResponsivePictureLayout } from '../Picture/ResponsivePictureLayouts'
import { RequiredAccess } from '@news-mono/web-common'

export interface CommonGalleryProps {
    onEvent: (event: GalleryEvent) => void
    className?: string
    adFrequency?: number
    // We can't know how many ads to init until we are loaded
    // because that is based on the number of images
    ads?: React.ReactElement<any>[]
}

export interface GalleryLoadingProps extends CommonGalleryProps {
    loading: true
}

export interface GalleryLoadedProps extends CommonGalleryProps {
    loading?: false
    title: string
    mainImage: ImageSourceWithCaptions | undefined
    images: ImageSourceWithCaptions[]
    shareUrl: string
    byline?: string
    profile?: Profile[]
    publicationDate: string
    source: string
    requiredAccess: RequiredAccess
    entitled: boolean
}

export type GalleryProps = GalleryLoadingProps | GalleryLoadedProps

export const Gallery: React.FC<GalleryProps> = (props) => {
    const [showThumbnails, setShowThumbnails] = useState(false)
    const [totalLoaded, setTotalLoaded] = useState(() => {
        if (props.loading || props.images.length > 0) {
            return numPerPage
        }
        return 0
    })
    const theme = useTheme()

    function loadMoreImages(numToLoad = numPerPage) {
        // If we are loading, assume we will get enough to fill the first page
        // TODO This could hurt viewability if an ad is loaded, then we remove it due to
        // less images in gallery?
        if (props.loading) {
            setTotalLoaded(numToLoad)
        } else if (totalLoaded < props.images.length) {
            setTotalLoaded(totalLoaded + numToLoad)
        }
    }

    useEffect(
        () => {
            if (props.loading) return

            setTotalLoaded(Math.min(props.images.length, numPerPage))
        },
        // Ignore props.images.length changing, the value is only relevant when props.loading becomes true
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.loading],
    )

    function toggleThumbnails() {
        if (showThumbnails) {
            props.onEvent({
                type: DataLayerEventName.galleryThumbnailsShow,
                originator: 'Gallery',
                payload: {},
            })
        } else {
            props.onEvent({
                type: DataLayerEventName.galleryThumbnailsHide,
                originator: 'Gallery',
                payload: {},
            })
        }
        setShowThumbnails(!showThumbnails)
    }

    async function scrollTo(index: number) {
        if (index + 1 > totalLoaded) {
            await loadMoreImages(index + 1 - totalLoaded)
        }
        const targetElement = document.querySelector(`#image-${index}`)

        if (!targetElement) {
            return
        }

        smoothScrollTo(targetElement)
    }

    function showMoreClicked() {
        props.onEvent({
            type: DataLayerEventName.galleryLoadMore,
            originator: 'Gallery',
            payload: {},
        })
        loadMoreImages()
    }

    function renderLoadMore() {
        return (
            !props.loading &&
            totalLoaded < props.images.length && (
                <StyledShowMoreButton type="button" onClick={showMoreClicked}>
                    Show More
                </StyledShowMoreButton>
            )
        )
    }

    const { className, onEvent } = props

    const mainImage = props.loading ? undefined : props.mainImage
    const caption = props.loading ? (
        <StyledHeroCaptionContainer>
            <Skeleton count={2} />
        </StyledHeroCaptionContainer>
    ) : (
        mainImage && (
            <React.Fragment>
                {theme.kind === 'perthnow' && (
                    <StyledHeroCaptionContainer>
                        <GalleryCaption
                            captionText={mainImage.captionText}
                            credit={mainImage.credit}
                            byline={mainImage.byline}
                        />
                        <StyledButton type="button" onClick={toggleThumbnails}>
                            {showThumbnails
                                ? 'Hide Thumbnails'
                                : 'Show Thumbnails'}
                        </StyledButton>
                    </StyledHeroCaptionContainer>
                )}

                {theme.kind === 'thewest' && (
                    <GalleryCaption
                        captionText={mainImage.captionText}
                        credit={mainImage.credit}
                        byline={mainImage.byline}
                    />
                )}
            </React.Fragment>
        )
    )

    const overlayImage = !props.loading && props.mainImage !== undefined
    // If there is a 16:9 we want to use that, but sometimes the wire feeds don't have proper crops, so we fallback to original so it displays an image
    const fixedRatioValue =
        !props.loading && props.mainImage && props.mainImage.crops['16:9']
            ? '16:9'
            : 'original'
    const desktopWidth = themedValue(theme, {
        thewest: 1224,
        perthnow: 984,
        sevennews: 650,
    })
    return (
        <StyledGallery className={className}>
            <StyledHeroWrap>
                {props.loading ? (
                    <PicturePlaceholder fixedRatio="16:9" image={undefined} />
                ) : props.mainImage ? (
                    <Picture
                        image={props.mainImage}
                        imageWidths={{
                            mobile: '100vw',
                            tablet: '100vw',
                            desktop: `${desktopWidth}px`,
                            fallbackWidth: desktopWidth,
                        }}
                        fixedRatio={fixedRatioValue}
                        layout={ResponsivePictureLayout.ObjectFitContain}
                    />
                ) : null}

                {theme.kind === 'perthnow' && !props.loading && (
                    <StyledTitleBar
                        entitled={props.entitled}
                        overlayImage={overlayImage}
                    >
                        <StyledTitle>
                            <StyledIcon />
                            {props.loading ? <Skeleton /> : props.title}
                        </StyledTitle>
                        {!props.loading && (
                            <StyledSharing
                                url={props.shareUrl}
                                text={props.title}
                                onEvent={onEvent}
                                shareOptions={[
                                    'facebook',
                                    'twitter',
                                    'email',
                                    'clipboard',
                                ]}
                            />
                        )}
                    </StyledTitleBar>
                )}

                {theme.kind === 'thewest' && !props.loading && (
                    <StyledTitleBar
                        entitled={props.entitled}
                        overlayImage={overlayImage}
                    >
                        {props.entitled && (
                            <StyledButton
                                type="button"
                                onClick={toggleThumbnails}
                            >
                                {showThumbnails
                                    ? 'Hide Thumbnails'
                                    : 'Show Thumbnails'}
                            </StyledButton>
                        )}
                        <StyledSharing
                            url={props.shareUrl}
                            text={props.title}
                            onEvent={onEvent}
                            shareOptions={[
                                'facebook',
                                'twitter',
                                'email',
                                'clipboard',
                            ]}
                        />
                    </StyledTitleBar>
                )}
            </StyledHeroWrap>
            <StyledGalleryContentWrapper>
                {theme.kind === 'thewest' && (
                    <React.Fragment>
                        <StyledTitle>
                            {props.loading ? <Skeleton /> : props.title}
                        </StyledTitle>

                        {caption}
                    </React.Fragment>
                )}

                {theme.kind === 'perthnow' && caption}

                {theme.kind === 'thewest' && !props.loading && (
                    <Byline
                        profile={props.profile}
                        text={props.byline}
                        timestamp={props.publicationDate}
                        source={props.source}
                        isFullWidth={true}
                        url={props.shareUrl}
                        title={props.title}
                        requiredAccess={props.requiredAccess}
                        allowCommenting={false}
                    />
                )}
            </StyledGalleryContentWrapper>
            {showThumbnails && !props.loading && props.entitled && (
                <GalleryThumbnails
                    images={props.images}
                    onEvent={onEvent}
                    scrollTo={scrollTo}
                    toggleThumbnails={toggleThumbnails}
                />
            )}
            <GalleryImages
                loading={props.loading}
                totalLoaded={totalLoaded}
                images={!props.loading ? props.images : []}
                adFrequency={props.adFrequency}
                ads={props.ads}
            />
            {renderLoadMore()}
        </StyledGallery>
    )
}
Gallery.displayName = 'Gallery'
